/**
 * Auth API hooks. Thin wrappers over TanStack Query that call the backend
 * and update the auth store. Adapted from PayrollSystem's hooks/api/auth.ts
 * — no signup, no tenant switching, no onboarding.
 */

import { useMutation, useQuery } from "@tanstack/react-query";

import { api, AxiosError } from "@/lib/api";
import { getRefreshToken } from "@/lib/auth";
import { useAuthStore } from "@/stores/auth";
import type { AuthSession, LanguagePreference, LoginInput, MeResponse } from "@/types/api";

// ---------- Login ----------

export function useLogin() {
  const setSession = useAuthStore((s) => s.setSession);
  return useMutation<AuthSession, AxiosError, LoginInput>({
    mutationFn: async (input) => {
      const r = await api.post<AuthSession>("/v1/auth/login/", input);
      return r.data;
    },
    onSuccess: (session) => setSession(session),
  });
}

// ---------- Logout ----------

export function useLogout() {
  const clearSession = useAuthStore((s) => s.clearSession);
  return useMutation({
    mutationFn: async () => {
      const refresh = getRefreshToken();
      if (refresh) {
        try {
          await api.post("/v1/auth/logout/", { refresh });
        } catch {
          // Logout is best-effort; clear local state regardless
        }
      }
    },
    onSettled: () => clearSession(),
  });
}

// ---------- Me (current user + role) ----------

export function useMe() {
  const isAuth = useAuthStore((s) => s.user !== null);
  const setUser = useAuthStore((s) => s.setUser);

  return useQuery<MeResponse>({
    queryKey: ["me"],
    queryFn: async () => {
      const r = await api.get<MeResponse>("/v1/auth/me/");
      // Backfill the store with details the JWT didn't carry
      setUser(r.data.user);
      return r.data;
    },
    enabled: isAuth,
    staleTime: 60_000,
    retry: 1,
  });
}

// ---------- Language preference (self-service) ----------

export function useUpdateLanguagePreference() {
  const setUser = useAuthStore((s) => s.setUser);
  return useMutation<MeResponse, AxiosError, LanguagePreference>({
    mutationFn: async (language_preference) => {
      const r = await api.patch<MeResponse>("/v1/auth/me/", { language_preference });
      return r.data;
    },
    onSuccess: (data) => setUser(data.user),
  });
}
